import { BasicApplicationService } from './BasicApplicationService';

/**
 * Adds a campaign tracking iframe to the rendering template.
 */
export class BasicCampaignTrackingService<T extends BasicAppDefinition> {
	private readonly applicationService: BasicApplicationService<T>;
	private readonly options: BasicAppOptions<T>;

	constructor(options: BasicAppOptions<T>) {
		this.options = options;
		this.applicationService = new BasicApplicationService(options);
	}

	/**
	 * setTracking.
	 *
	 * Adds the tracking iframe to the template depending on the current page and user status.
	 *
	 * @param template - the template to add the tracking iframe to.
	 * @return the original template or the template + added tracking iframe.
	 */
	public setTracking(template: string, transactionkey?: string, enclosedproducts?: boolean[]): string {
		return this.setTrackingInfoToTemplate(template, this.options.app.campaignurls, transactionkey, enclosedproducts);
	}

	/**
	 * setTrackingInfoToTemplate.
	 *
	 * Adds the tracking iframe to the template depending on the current page and user status.
	 *
	 * @param template - the template to add the tracking iframe to.
	 * @param campaignUrls - the campaign urls to use for the tracking iframe.
	 * @return the original - template or the template + added tracking iframe.
	 */
	private setTrackingInfoToTemplate(template: string, campaignUrls: { [key: string]: any[] }, transactionkey?: string, enclosedproducts?: boolean[]): string {
		let result = template;
		if (campaignUrls) {
			const effectiveKey = this.getEffectiveCampaKey();
			const trackingSrcs = effectiveKey ? campaignUrls[effectiveKey] : undefined;
			if (trackingSrcs) {
				for (let i = 0; i < trackingSrcs.length; i++) {
					let trackingSrc = this.replaceTransactionKeyParam(trackingSrcs[i], transactionkey);
					trackingSrc = this.replaceProductKey(trackingSrc, enclosedproducts);
					result += `<iframe id="kampa${i + 1}" src="${trackingSrc}" width="1" height="1" frameborder="0" data-html2canvas-ignore="true"></iframe>`;
				}
			}
		}

		return result;
	}

	/**
	 * Computes key into campaignUrls map that is effective in current application state,
	 * default implementation only matches current application step name such as "form" or "success"
	 * @return campaign key or "", if not applicable
	 */
	protected getEffectiveCampaKey(): string {
		return this.applicationService.getCurrentStepName();
	}

	/**
	 * Replaces placholder {transactionkey} in pixel url if given, or nothing if no transaction key
	 * present
	 * @param trackingSrc - url for campaign pixel
	 * @param transactionkey - transaction key from APIL backend, if one is given
	 */
	private replaceTransactionKeyParam(trackingSrc: string, transactionkey?: string): string {
		return trackingSrc.replace("{transactionkey}", encodeURIComponent(transactionkey || ""));
	}

	/**
	 * Replaces placeholder {productkey} with acronym of products the user selected
	 * @param trackingSrc - url for campaign pixel
	 * @param enclosedproducts - boolean array of products the user selected, i.e.
	 * enclosedproduct[1] == true => second product selected
	 */
	private replaceProductKey(trackingSrc: string, enclosedproducts?: boolean[]): string {
		let replaceString = "";
		const productkeys = this.options.app.campaignproductkeys;
		if (productkeys && productkeys.length) {
			if (enclosedproducts) {
				//more than one product
				const products: string[] = [];
				for (let i = 0; i < enclosedproducts.length; i++) {
					if (enclosedproducts[i] && productkeys.length > i) {
						products.push(productkeys[i]);
					}
				}
				replaceString = products.join(';');
			} else {
				//only one product in form
				replaceString = productkeys[0];
			}
		}
		return trackingSrc.replace("{productkey}", encodeURIComponent(replaceString));
	}

}
